import { Box, useTheme } from "@mui/material";
/**
 * Shared chassis every ComponentKind symbol renders inside. Holds:
 *
 *   * Four invisible react-flow Handles (top/right/bottom/left) so
 *     edges can attach on any side. The IR's pin-side derivation lives
 *     in `irToElkGraph.ts` — react-flow doesn't care which one ELK
 *     picked, it just needs an attachment point.
 *   * The node body slot (children — the symbol's SVG).
 *   * A refdes label below the symbol (mono, faint).
 *
 * Per-pin Handles (one per Pin) are V2 work — would require threading
 * port coordinates from the ELK layout through to react-flow. For the
 * chat preview the four-side default handles read fine.
 */
import { Handle, Position } from "@xyflow/react";
import type { ReactNode } from "react";

import { LABEL_STYLE } from "./symbolStyle";

interface SymbolFrameProps {
  refdes?: string;
  value?: string | null;
  /** Pixel size hints — match what irToElkGraph.nodeSize emitted. */
  width?: number;
  height?: number;
  /** When true, render a cyan halo around the symbol — used by the
   *  RenameTheatreDialog (and future BOM-row → canvas flash) to focus
   *  the user's attention on a specific component. */
  highlight?: boolean;
  children: ReactNode;
}

export function SymbolFrame({
  refdes,
  value,
  width,
  height,
  highlight,
  children,
}: SymbolFrameProps) {
  const t = useTheme();
  return (
    <Box
      sx={{
        position: "relative",
        width,
        height,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        // Halo lives as an absolutely-positioned pseudo-element so it
        // doesn't steal layout space — the symbol stays centered and
        // the glow extends slightly past the node bounding box.
        ...(highlight && {
          "&::before": {
            content: '""',
            position: "absolute",
            inset: -6,
            borderRadius: 8,
            border: `1.5px solid ${t.palette.accent.main}`,
            boxShadow: t.palette.accent.glow,
            pointerEvents: "none",
            animation: "traceStrokeIn 220ms ease-out",
          },
        }),
      }}
    >
      {/* Four-side handles — react-flow uses the closest one for an
          inbound edge. Visually transparent so they don't compete with
          the symbol stroke. */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: "transparent", border: "none" }}
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: "transparent", border: "none" }}
        isConnectable={false}
      />
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "transparent", border: "none" }}
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "transparent", border: "none" }}
        isConnectable={false}
      />

      <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
        {children}
      </Box>

      {(refdes || value) && (
        <Box
          sx={{
            position: "absolute",
            bottom: -2,
            display: "flex",
            gap: 0.75,
            alignItems: "baseline",
            ...LABEL_STYLE,
          }}
        >
          {refdes && (
            <Box sx={{ color: t.palette.text.primary, fontWeight: 600 }}>
              {refdes}
            </Box>
          )}
          {value && <Box sx={{ color: t.palette.text.secondary }}>{value}</Box>}
        </Box>
      )}
    </Box>
  );
}
